import { routes, type Lang } from "./routes";

type RouteTuple = [string, string];

/**
 * Get the route tuple for a given route key
 * @param key Route key (e.g. 'features.scheduling')
 * @returns Tuple of [svPath, enPath] or undefined if not found
 */
export function getRouteTuple(key: string): RouteTuple | undefined {
  const keys = key.split(".");
  let current: unknown = routes;

  // Traverse the routes object using the keys
  for (const k of keys) {
    if (
      typeof current === "object" &&
      current !== null &&
      !Array.isArray(current) &&
      k in current
    ) {
      current = (current as Record<string, unknown>)[k];
    } else {
      return undefined;
    }
  }

  // Check if the current value is a tuple (array of two strings)
  if (
    Array.isArray(current) &&
    current.length === 2 &&
    typeof current[0] === "string" &&
    typeof current[1] === "string"
  ) {
    return current as RouteTuple;
  }

  return undefined;
}

/**
 * Get the localized path for a given path or route key
 */
export function getLocalizedPath(keyOrPath: string, targetLang: Lang): string {
  // Handle the case where we're passing a key like 'home', 'products', etc.
  const routeTuple = getRouteTuple(keyOrPath);
  if (routeTuple) {
    return targetLang === "sv" ? routeTuple[0] : routeTuple[1];
  }

  // If not a key, we're dealing with a path - continue with path-based logic

  // Handle special cases
  if (!keyOrPath || keyOrPath === "/") {
    return targetLang === "en" ? "/en" : "/";
  }

  // Handle special case for home route
  if (keyOrPath === "/en" && targetLang === "sv") {
    return "/";
  }

  // Search for this path in routes
  for (const key in routes) {
    const route = routes[key as keyof typeof routes];

    // Handle direct routes
    if (Array.isArray(route)) {
      if (route[0] === keyOrPath && targetLang === "en") return route[1];
      if (route[1] === keyOrPath && targetLang === "sv") return route[0];
    } else if (typeof route === "object") {
      // Search nested objects (e.g., features, articles)
      for (const nestedKey in route) {
        const nestedRoute = route[nestedKey] as [string, string];
        if (Array.isArray(nestedRoute) && nestedRoute.length === 2) {
          if (nestedRoute[0] === keyOrPath && targetLang === "en")
            return nestedRoute[1];
          if (nestedRoute[1] === keyOrPath && targetLang === "sv")
            return nestedRoute[0];
        }
      }
    }
  }

  // For unknown paths, we'll keep the original path
  // This preserves non-standard URL paths like /non-existent
  return keyOrPath;
}

/**
 * Find the route key for a given path
 * @param path Path to find (e.g. '/funktioner/schemaläggning')
 * @returns Route key or undefined if not found
 */
export function findRouteKey(path: string): string | undefined {
  // Normalize path if it has trailing slash
  const normalizedPath =
    path.endsWith("/") && path !== "/" ? path.slice(0, -1) : path;

  // Flat map of path to route key
  const pathToKey = new Map<string, string>();

  // Helper function to add paths to the map
  const addToMap = (key: string, tuple: [string, string]) => {
    pathToKey.set(tuple[0], key); // Swedish path
    pathToKey.set(tuple[1], key); // English path
  };

  // Process all routes
  Object.entries(routes).forEach(([key, value]) => {
    if (
      Array.isArray(value) &&
      value.length === 2 &&
      typeof value[0] === "string" &&
      typeof value[1] === "string"
    ) {
      addToMap(key, value as [string, string]);
    } else if (typeof value === "object") {
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        if (
          Array.isArray(nestedValue) &&
          nestedValue.length === 2 &&
          typeof nestedValue[0] === "string" &&
          typeof nestedValue[1] === "string"
        ) {
          addToMap(`${key}.${nestedKey}`, nestedValue as [string, string]);
        }
      });
    }
  });

  return pathToKey.get(normalizedPath);
}
